import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons';

/**
 * Password rules for the invite set-password screen (U5.2). The three
 * "required" rules gate the submit button; the rest only raise the strength
 * score. The server stays the authority — its message is shown if it asks
 * for more.
 */
export const PASSWORD_RULES = [
  {
    id: 'length',
    label: 'At least 8 characters',
    required: true,
    test: value => value.length >= 8
  },
  {
    id: 'letter',
    label: 'A letter',
    required: true,
    test: value => /[A-Za-z]/.test(value)
  },
  {
    id: 'number',
    label: 'A number',
    required: true,
    test: value => /\d/.test(value)
  },
  {
    id: 'case',
    label: 'Upper & lower case',
    required: false,
    test: value => /[a-z]/.test(value) && /[A-Z]/.test(value)
  },
  {
    id: 'symbol',
    label: 'A symbol, like ! or #',
    required: false,
    test: value => /[^A-Za-z0-9\s]/.test(value)
  },
  {
    id: 'long',
    label: '12 or more characters',
    required: false,
    test: value => value.length >= 12
  }
];

const LEVELS = [
  { bars: 0, label: '', color: 'var(--falcon-gray-500)' },
  { bars: 1, label: 'Too weak', color: '#e63757' },
  { bars: 2, label: 'Fair', color: '#f5a23e' },
  { bars: 3, label: 'Good', color: '#27bcfd' },
  { bars: 4, label: 'Strong', color: '#00b066' }
];

export const evaluatePassword = (password = '') => {
  const value = String(password);
  const checks = PASSWORD_RULES.map(rule => ({
    id: rule.id,
    label: rule.label,
    required: rule.required,
    met: rule.test(value)
  }));
  const requiredMet = checks
    .filter(check => check.required)
    .every(check => check.met);
  const extras = checks.filter(check => !check.required && check.met).length;

  let level = LEVELS[0];
  if (value && !requiredMet) {
    level = LEVELS[1];
  } else if (value) {
    level = LEVELS[Math.min(2 + extras, 4)];
  }

  return { checks, requiredMet, level };
};

const PasswordStrengthMeter = ({ password, id }) => {
  const { checks, level } = evaluatePassword(password);

  return (
    <div
      id={id}
      className="mym-strength"
      style={{ '--mym-strength-color': level.color }}
    >
      <div className="mym-strength-head">
        <span className="text-600">Password strength</span>
        <span className="mym-strength-label" aria-live="polite">
          {level.label}
        </span>
      </div>
      <div className="mym-strength-bars" aria-hidden="true">
        {[1, 2, 3, 4].map(bar => (
          <span
            key={bar}
            className={classNames('mym-strength-bar', {
              'is-on': bar <= level.bars
            })}
          />
        ))}
      </div>
      <ul className="mym-rules">
        {checks.map(check => (
          <li
            key={check.id}
            className={classNames('mym-rule', { 'is-met': check.met })}
          >
            <span className="mym-rule-icon" aria-hidden="true">
              <FontAwesomeIcon icon={faCheck} />
            </span>
            <span>
              {check.label}
              {!check.required && (
                <span className="mym-rule-tag"> · bonus</span>
              )}
            </span>
            <span className="visually-hidden">
              {check.met ? ' — done' : ' — not yet'}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};

PasswordStrengthMeter.propTypes = {
  password: PropTypes.string,
  id: PropTypes.string
};

export default PasswordStrengthMeter;
